import React, { useState, lazy, Suspense } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Form,
  Input,
  Typography,
  Select,
  useToast,
  Loading,
} from '@crisellevaldez/speculo-ui';
import { AlertCircle } from 'lucide-react';

// Lazy load DNS Records Section
const DNSRecordsSection = lazy(() =>
  import('../components/DNSRecordsSection').then((module) => ({
    default: module.DNSRecordsSection,
  }))
);

// Lazy load GoDaddy Delegate Access component
const GoDaddyDelegateAccess = lazy(() =>
  import('../components/GoDaddyDelegateAccess').then((module) => ({
    default: module.GoDaddyDelegateAccess,
  }))
);
import { idxSiteSetupSchema, IDXSiteSetupData } from '../schemas/onboarding.schema';
import { FormSection } from '../components/FormSection';
import { TipBox } from '../components/TipBox';
import { QuestionLabel } from '../components/QuestionLabel';
import { NextButton } from '../components/NextButton';
import { StageContainer } from '../components/StageContainer';
import { SectionBox } from '../components/SectionBox';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useOnboardingWebhook } from '../hooks/useOnboardingWebhook';

interface IDXSiteSetupStepProps {
  onNext: (data: IDXSiteSetupData) => void;
  onBack?: () => void;
  initialData?: Partial<IDXSiteSetupData>;
  isLoading?: boolean;
  websiteData?: any; // Data from Stage 2
  orgName?: string;
  brokerageName?: string;
}

export const IDXSiteSetupStep: React.FC<IDXSiteSetupStepProps> = ({
  onNext,
  onBack,
  initialData,
  isLoading = false,
  websiteData,
  orgName = '',
  brokerageName = '',
}) => {
  const { organization } = useOrganizationStore();
  const { showToast } = useToast();
  const { sendWebhook } = useOnboardingWebhook();

  // Determine website type from Stage 2 data
  const hasWebsite = websiteData?.is_primary_website === 'yes' ? 'primary' : 'no';

  const websiteDomain = websiteData?.domain_name || '';

  // Check if Speculo purchased the domain - no DNS help needed
  const speculoPurchasedDomain = websiteData?.domain_ownership === 'purchase';

  // Get existing logo URL from various sources
  const existingLogoUrl =
    initialData?.logo_url ||
    (organization as any)?.onboardingData?.website?.website_builder?.hero?.logo ||
    organization?.logoUrl ||
    '';

  const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(existingLogoUrl);
  const [selectedFavicon, setSelectedFavicon] = useState<File | null>(null);
  const [faviconPreviewUrl, setFaviconPreviewUrl] = useState(
    (initialData as any)?.favicon_url || organization?.faviconUrl || ''
  );
  const [logoSize, setLogoSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [logoPlacement, setLogoPlacement] = useState<'header' | 'both'>('header');
  const [dnsOption, setDnsOption] = useState<'add_dns' | 'need_help' | null>(null);
  const [domainAccessShared, setDomainAccessShared] = useState(false);
  const [dnsRecordsAdded, setDnsRecordsAdded] = useState(false);

  // Track which button action is in progress: 'skip' | 'launch' | null
  const [actionInProgress, setActionInProgress] = useState<'skip' | 'launch' | null>(null);

  // Compliance links state
  const [complianceLinks, setComplianceLinks] = useState<{ key: string; value: string }[]>([]);
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');

  const logoSizes = {
    small: { width: 100, height: 50 },
    medium: { width: 200, height: 100 },
    large: { width: 260, height: 130 },
  };

  const form = useForm<IDXSiteSetupData>({
    resolver: zodResolver(idxSiteSetupSchema),
    defaultValues: {
      has_website: initialData?.has_website || hasWebsite,
      idx_subdomain: initialData?.idx_subdomain || '',
      new_domain: initialData?.new_domain || '',
      website_domain: websiteDomain,
      logo_url: existingLogoUrl, // Use existing logo URL for validation
      primary_color: initialData?.primary_color || '#000000',
      secondary_color: initialData?.secondary_color || '#000000',
      privacy_policy_url: initialData?.privacy_policy_url || '',
      terms_of_service_url: initialData?.terms_of_service_url || '',
      disclaimer_url: initialData?.disclaimer_url || '',
      // When Speculo purchased the domain, force needs_domain_help to false (no DNS help needed)
      needs_domain_help: speculoPurchasedDomain ? false : initialData?.needs_domain_help || false,
      domain_access_shared: speculoPurchasedDomain
        ? true
        : initialData?.domain_access_shared || false,
      // No auto-fill for registrar fields - always start empty
      domain_registrar: '',
      registrar_username: '',
      registrar_password: '',
      registrar_email: '',
      registrar_phone: '',
    },
    mode: 'onBlur',
  });

  const watchHasWebsite = form.watch('has_website');
  const watchIdxSubdomain = form.watch('idx_subdomain');
  const watchNewDomain = form.watch('new_domain');

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        setLogoPreviewUrl(dataUrl);
        // Sync to form for validation - use data URL as placeholder to satisfy validation
        form.setValue('logo_url', dataUrl, { shouldValidate: true });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFavicon(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFaviconPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    showToast({
      message: `${label} copied to clipboard`,
      variant: 'success',
    });
  };

  const handleAddLink = () => {
    if (newLinkTitle && newLinkUrl) {
      setComplianceLinks([
        ...complianceLinks,
        {
          key: newLinkTitle,
          value: newLinkUrl.startsWith('https://') ? newLinkUrl : `https://${newLinkUrl}`,
        },
      ]);
      setNewLinkTitle('');
      setNewLinkUrl('');
    }
  };

  const handleDeleteLink = (linkKey: string) => {
    setComplianceLinks((prevLinks) => prevLinks.filter((link) => link.key !== linkKey));
  };

  const buildSubmissionData = (data: IDXSiteSetupData, skipped: boolean) => {
    return {
      ...data,
      logo_file: selectedLogo,
      favicon_file: selectedFavicon,
      logo_width: logoSizes[logoSize].width,
      logo_height: logoSizes[logoSize].height,
      logo_placement: logoPlacement,
      compliance_links: complianceLinks,
      idx_skipped: skipped,
    };
  };

  const handleSubmit = (data: IDXSiteSetupData) => {
    // Validate that DNS records checkbox is checked before launching
    // Skip validation if Speculo purchased the domain (we handle DNS)
    if (!speculoPurchasedDomain && !dnsRecordsAdded) {
      showToast({
        message: 'Please confirm that you have added the DNS records before launching',
      });
      return;
    }

    // Set loading state for launch button
    setActionInProgress('launch');
    // Pass file objects, size, placement, and compliance links to parent
    const submissionData = buildSubmissionData(data, false);
    onNext(submissionData as any);
  };

  const handleSkipSubmit = (data: IDXSiteSetupData) => {
    // Set loading state for skip button
    setActionInProgress('skip');
    // Save current form data but mark as skipped for different webhook handling
    const submissionData = buildSubmissionData(data, true);
    onNext(submissionData as any);
  };

  // Calculate full subdomain URL
  const getFullSubdomainUrl = () => {
    const subdomain = watchIdxSubdomain;
    if (!subdomain) return '';

    if (watchHasWebsite === 'no' && watchNewDomain) {
      return `${subdomain}.${watchNewDomain}`;
    } else if (websiteDomain) {
      return `${subdomain}.${websiteDomain}`;
    }
    return subdomain;
  };

  return (
    <StageContainer
      title="IDX Site Setup"
      description="Configure your IDX website domain, branding, and DNS settings."
    >
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        {/* Domain Configuration */}
        <div className="space-y-6">
          <Typography variant="h2" className="text-xl font-semibold">
            Domain Configuration
          </Typography>

          {watchHasWebsite === 'no' ? (
            <>
              <FormSection>
                <Form.Control required>
                  <QuestionLabel>New Domain for IDX Site</QuestionLabel>
                  <Controller
                    name="new_domain"
                    control={form.control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="example.com"
                        error={form.formState.errors.new_domain?.message}
                      />
                    )}
                  />
                  <TipBox>
                    Enter the domain you want to use for your IDX site (e.g., yourcompany.com)
                  </TipBox>
                </Form.Control>
              </FormSection>

              <FormSection>
                <Form.Control required>
                  <QuestionLabel>IDX Subdomain</QuestionLabel>
                  <Controller
                    name="idx_subdomain"
                    control={form.control}
                    render={({ field }) => (
                      <div className="flex items-center gap-2">
                        <Input
                          {...field}
                          placeholder="homes"
                          error={form.formState.errors.idx_subdomain?.message}
                          className="flex-1"
                        />
                        <span className="text-gray-500">.{watchNewDomain || 'domain.com'}</span>
                      </div>
                    )}
                  />
                  <TipBox>The subdomain for your IDX property search site</TipBox>
                </Form.Control>
              </FormSection>
            </>
          ) : (
            <FormSection>
              <Form.Control required>
                <QuestionLabel>IDX Subdomain</QuestionLabel>
                <Controller
                  name="idx_subdomain"
                  control={form.control}
                  render={({ field }) => (
                    <div className="flex items-center gap-2">
                      <Input
                        {...field}
                        placeholder="homes"
                        error={form.formState.errors.idx_subdomain?.message}
                        className="flex-1"
                      />
                      <span className="text-gray-500">.{websiteDomain || 'domain.com'}</span>
                    </div>
                  )}
                />
                <TipBox>
                  Your IDX site will be accessible at: <strong>{getFullSubdomainUrl()}</strong>
                </TipBox>
              </Form.Control>
            </FormSection>
          )}
        </div>

        {/* Branding */}
        <div className="space-y-6">
          <Typography variant="h2" className="text-xl font-semibold">
            Branding
          </Typography>

          <FormSection>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <Form.Control required>
                <QuestionLabel>Logo</QuestionLabel>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('logo-upload')?.click()}
                    >
                      Browse
                    </Button>
                    <Select
                      value={logoSize}
                      onChange={(value) => {
                        if (typeof value === 'string') {
                          setLogoSize(value as 'small' | 'medium' | 'large');
                        }
                      }}
                      options={[
                        { value: 'small', label: 'Small' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'large', label: 'Large' },
                      ]}
                    />
                    <Select
                      value={logoPlacement}
                      onChange={(value) => {
                        if (typeof value === 'string') {
                          setLogoPlacement(value as 'header' | 'both');
                        }
                      }}
                      options={[
                        { value: 'header', label: 'Header Only' },
                        { value: 'both', label: 'Header & Footer' },
                      ]}
                    />
                  </div>
                  {(selectedLogo || logoPreviewUrl) && (
                    <div className="flex justify-start">
                      <div
                        className="rounded-md overflow-hidden bg-gray-50"
                        style={{
                          width: logoSizes[logoSize].width,
                          height: logoSizes[logoSize].height,
                        }}
                      >
                        <img
                          src={selectedLogo ? URL.createObjectURL(selectedLogo) : logoPreviewUrl}
                          alt="Logo preview"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  )}
                </div>
                {form.formState.errors.logo_url?.message && (
                  <p className="text-sm text-red-500 mt-1">
                    {form.formState.errors.logo_url.message}
                  </p>
                )}
                <TipBox>Logo must meet MLS and Brokerage Compliance standards.</TipBox>
              </Form.Control>
              {/* Hidden file input moved outside Form.Control to avoid required attribute issue */}
              <input
                id="logo-upload"
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleLogoChange}
              />

              <div>
                <Form.Control>
                  <QuestionLabel required={false}>Favicon</QuestionLabel>
                  <div className="flex items-center gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('favicon-upload')?.click()}
                    >
                      Browse
                    </Button>
                    {(selectedFavicon || faviconPreviewUrl) && (
                      <div className="w-8 h-8 rounded-md overflow-hidden bg-gray-50">
                        <img
                          src={
                            selectedFavicon
                              ? URL.createObjectURL(selectedFavicon)
                              : faviconPreviewUrl
                          }
                          alt="Favicon preview"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                  </div>
                </Form.Control>
                {/* Hidden file input moved outside Form.Control for consistency */}
                <input
                  id="favicon-upload"
                  type="file"
                  className="hidden"
                  accept="image/x-icon,image/png"
                  onChange={handleFaviconChange}
                />
              </div>
            </div>
          </FormSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSection>
              <Form.Control>
                <QuestionLabel>Primary Color</QuestionLabel>
                <Controller
                  name="primary_color"
                  control={form.control}
                  render={({ field }) => (
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="h-10 w-10 rounded-md cursor-pointer border"
                      />
                      <Input {...field} className="uppercase flex-1" />
                    </div>
                  )}
                />
              </Form.Control>
            </FormSection>

            <FormSection>
              <Form.Control>
                <QuestionLabel>Secondary Color</QuestionLabel>
                <Controller
                  name="secondary_color"
                  control={form.control}
                  render={({ field }) => (
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="h-10 w-10 rounded-md cursor-pointer border"
                      />
                      <Input {...field} className="uppercase flex-1" />
                    </div>
                  )}
                />
              </Form.Control>
            </FormSection>
          </div>
        </div>

        {/* Compliance Links */}
        <div className="space-y-6">
          <Typography variant="h2" className="text-xl font-semibold">
            Compliance Links
          </Typography>

          <FormSection>
            <TipBox>Compliance links appear at the footer of your page.</TipBox>

            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-[1fr,2fr,auto] gap-4">
                <Input
                  placeholder="Enter title"
                  value={newLinkTitle}
                  onChange={(e) => setNewLinkTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newLinkTitle && newLinkUrl) {
                        handleAddLink();
                      }
                    }
                  }}
                />
                <Input
                  placeholder="https://example.com"
                  value={newLinkUrl}
                  onChange={(e) => setNewLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newLinkTitle && newLinkUrl) {
                        handleAddLink();
                      }
                    }
                  }}
                />
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setNewLinkTitle('');
                      setNewLinkUrl('');
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleAddLink}
                    disabled={!newLinkTitle || !newLinkUrl}
                  >
                    Add Link
                  </Button>
                </div>
              </div>

              {complianceLinks.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-2 text-sm font-semibold">Title</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold">URL</th>
                        <th className="text-center px-4 py-2 text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {complianceLinks.map((link, index) => (
                        <tr key={index} className="border-b last:border-b-0">
                          <td className="px-4 py-2 text-sm">{link.key}</td>
                          <td className="px-4 py-2 text-sm truncate max-w-xs">{link.value}</td>
                          <td className="px-4 py-2 text-center">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteLink(link.key)}
                              className="text-white hover:bg-red-600 bg-red-700 border-0"
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-gray-500 py-4 border rounded-lg">
                  No compliance links added yet
                </div>
              )}
            </div>
          </FormSection>
        </div>

        {/* DNS Help Options - Only show if user owns the domain (not purchased by Speculo) */}
        {!speculoPurchasedDomain && (
          <div className="space-y-6">
            <Typography variant="h2" className="text-xl font-semibold">
              Need Help Adding DNS Records?
            </Typography>

            <FormSection>
              <div className="space-y-4">
                {/* Option 1: Add DNS Records and Launch */}
                <div
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    dnsOption === 'add_dns'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => {
                    setDnsOption('add_dns');
                    form.setValue('needs_domain_help', false);
                  }}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="dns_option"
                      checked={dnsOption === 'add_dns'}
                      onChange={() => {
                        setDnsOption('add_dns');
                        form.setValue('needs_domain_help', false);
                      }}
                      className="w-4 h-4 mt-1"
                    />
                    <div className="flex-1">
                      <Typography variant="h3" className="text-sm font-semibold">
                        No, Add DNS Records and Launch your IDX now
                      </Typography>
                      <p className="text-sm text-gray-600 mt-1">
                        I will add the DNS records myself and verify them before launching
                      </p>
                    </div>
                  </div>
                </div>

                {/* Option 2: Need Help */}
                <div
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    dnsOption === 'need_help'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => {
                    setDnsOption('need_help');
                    form.setValue('needs_domain_help', true);
                  }}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="dns_option"
                      checked={dnsOption === 'need_help'}
                      onChange={() => {
                        setDnsOption('need_help');
                        form.setValue('needs_domain_help', true);
                      }}
                      className="w-4 h-4 mt-1"
                    />
                    <div className="flex-1">
                      <Typography variant="h3" className="text-sm font-semibold">
                        Yes, I need help with adding DNS records
                      </Typography>
                      <p className="text-sm text-gray-600 mt-1">
                        I need assistance configuring my domain - please help me set it up
                      </p>
                    </div>
                  </div>
                </div>

                {/* Content for Option 1: Add DNS Records - Show DNS Records */}
                {dnsOption === 'add_dns' && (
                  <SectionBox variant="gray" type="child">
                    {/* Lazy loaded DNS Records Section */}
                    <Suspense fallback={<Loading />}>
                      <DNSRecordsSection
                        subdomain={watchIdxSubdomain || ''}
                        checked={dnsRecordsAdded}
                        onCheckedChange={(checked) => {
                          if (!watchIdxSubdomain?.trim()) {
                            showToast({
                              message: 'Please enter the IDX subdomain first',
                            });
                            return;
                          }
                          setDnsRecordsAdded(checked);
                          // Send webhook when checkbox is checked
                          if (checked) {
                            sendWebhook('Stage 4 - DNS Records Added Confirmation', {
                              idx_subdomain: watchIdxSubdomain,
                              full_domain: getFullSubdomainUrl(),
                              dns_records_confirmed: true,
                              timestamp: new Date().toISOString(),
                            });
                            showToast({
                              message: 'Thank you! DNS records confirmation received.',
                              variant: 'success',
                            });
                          }
                        }}
                        showCheckbox={true}
                        checkboxLabel="I have added the DNS records"
                        checkboxDisabled={!watchIdxSubdomain?.trim()}
                        helperMessage={
                          !watchIdxSubdomain?.trim()
                            ? 'Please fill in the IDX subdomain above before confirming'
                            : undefined
                        }
                      />
                    </Suspense>
                  </SectionBox>
                )}

                {/* Content for Option 2: Need Help - Lazy loaded GoDaddy Delegate Access */}
                {dnsOption === 'need_help' && (
                  <SectionBox variant="gray" type="child">
                    <div className="space-y-4">
                      <Suspense fallback={<Loading />}>
                        <GoDaddyDelegateAccess
                          checked={domainAccessShared}
                          onCheckedChange={(checked) => {
                            if (!watchIdxSubdomain?.trim()) {
                              showToast({
                                message: 'Please enter the IDX subdomain first',
                              });
                              return;
                            }
                            setDomainAccessShared(checked);
                            // Sync to form for validation
                            form.setValue('domain_access_shared', checked);
                            // Send webhook when checkbox is checked
                            if (checked) {
                              sendWebhook('Stage 4 - IDX Domain Access Shared', {
                                idx_subdomain: watchIdxSubdomain,
                                full_domain: getFullSubdomainUrl(),
                                domain_access_shared: true,
                                timestamp: new Date().toISOString(),
                              });
                              showToast({
                                message: 'Thank you! We will verify the access soon.',
                                variant: 'success',
                              });
                            }
                          }}
                          showCheckbox={true}
                          checkboxLabel="I have already shared domain access with Speculo"
                          checkboxDisabled={!watchIdxSubdomain?.trim()}
                          helperMessage={
                            !watchIdxSubdomain?.trim()
                              ? 'Please fill in the IDX subdomain above before confirming'
                              : undefined
                          }
                        />
                      </Suspense>

                      {!domainAccessShared && (
                        <div className="space-y-4">
                          <Typography variant="h3" className="text-sm font-semibold">
                            Domain Registrar Information
                          </Typography>

                          <Form.Control>
                            <QuestionLabel>Where did you purchase your domain?</QuestionLabel>
                            <Controller
                              name="domain_registrar"
                              control={form.control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  placeholder="e.g., GoDaddy, Google Domains, Namecheap"
                                  error={form.formState.errors.domain_registrar?.message}
                                />
                              )}
                            />
                          </Form.Control>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Form.Control>
                              <QuestionLabel>Registrar Username</QuestionLabel>
                              <Controller
                                name="registrar_username"
                                control={form.control}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    placeholder="Your account username"
                                    error={form.formState.errors.registrar_username?.message}
                                  />
                                )}
                              />
                            </Form.Control>

                            <Form.Control>
                              <QuestionLabel>Registrar Password</QuestionLabel>
                              <Controller
                                name="registrar_password"
                                control={form.control}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    type="password"
                                    placeholder="Your account password"
                                    error={form.formState.errors.registrar_password?.message}
                                  />
                                )}
                              />
                            </Form.Control>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Form.Control>
                              <QuestionLabel>Account Owner Email</QuestionLabel>
                              <Controller
                                name="registrar_email"
                                control={form.control}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    type="email"
                                    placeholder="email@example.com"
                                    error={form.formState.errors.registrar_email?.message}
                                  />
                                )}
                              />
                              <TipBox>Needed for 2FA verification</TipBox>
                            </Form.Control>

                            <Form.Control>
                              <QuestionLabel>Account Owner Phone</QuestionLabel>
                              <Controller
                                name="registrar_phone"
                                control={form.control}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    type="tel"
                                    placeholder="(555) 123-4567"
                                    error={form.formState.errors.registrar_phone?.message}
                                  />
                                )}
                              />
                              <TipBox>Needed for 2FA verification</TipBox>
                            </Form.Control>
                          </div>
                        </div>
                      )}
                    </div>
                  </SectionBox>
                )}
              </div>
            </FormSection>
          </div>
        )}

        {/* Go-Live Status Notice */}
        {dnsOption === 'add_dns' && !speculoPurchasedDomain && (
          <FormSection variant="primary">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <Typography variant="h3" className="text-sm font-semibold text-yellow-900 mb-2">
                  Go-Live Status
                </Typography>
                <Typography variant="p" className="text-sm text-yellow-900">
                  Your IDX site will begin pushing DNS records upon completion. This propagation
                  process can take up to 24 hours for all internet servers to update. Check back
                  soon! P.S. Property data pending MLS approval ⏳
                </Typography>
              </div>
            </div>
          </FormSection>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center gap-3">
          {onBack ? (
            <NextButton type="button" onClick={onBack} variant="secondary" disabled={isLoading}>
              Back to MLS Setup
            </NextButton>
          ) : (
            <div />
          )}
          <div className="flex gap-3">
            {/* Show Next button only for "need_help" option */}
            {dnsOption === 'need_help' && (
              <NextButton
                type="button"
                onClick={form.handleSubmit(handleSkipSubmit)}
                isLoading={isLoading && actionInProgress === 'skip'}
                disabled={isLoading && actionInProgress !== 'skip'}
              >
                Next
              </NextButton>
            )}
            {/* Show Launch button only for "add_dns" option OR when Speculo purchased domain */}
            {(dnsOption === 'add_dns' || speculoPurchasedDomain) && (
              <NextButton
                type="submit"
                isLoading={isLoading && actionInProgress === 'launch'}
                disabled={isLoading && actionInProgress !== 'launch'}
              >
                Launch IDX NOW
              </NextButton>
            )}
            {/* Hide buttons when no option selected and domain not purchased - only show back button */}
          </div>
        </div>
      </form>
    </StageContainer>
  );
};
